/**
 * pickup-availability-drawer.js
 *
 * Independent "Order & Collect" custom element for the Cart Drawer.
 * Intentionally isolated from the PDP pickup-availability custom element.
 *
 * Custom element tag : <pickup-availability-cart>
 * LocalStorage key   : selectedStore  { name, siteup }
 * Inventory warning  : delegates to cart-drawer.loadCartInventory() → #InventoryWarning
 * site_code source   : localStorage.selectedStore.siteup (same as cart-drawer.js)
 */

(function () {
  "use strict";

  /* ── helpers ──────────────────────────────────────────────── */

  function escHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  /* ── custom element ───────────────────────────────────────── */

  if (customElements.get("pickup-availability-cart")) return;

  customElements.define(
    "pickup-availability-cart",
    class PickupAvailabilityCart extends HTMLElement {
      /* ── lifecycle ─────────────────────────────────────────── */

      connectedCallback() {
        this._variantId = this.dataset.variantId;
        const root = this.dataset.rootUrl || "/";
        this._rootUrl = root.endsWith("/") ? root : root + "/";
        this._onSelectedStoreChanged = this._onSelectedStoreChanged.bind(this);

        window.addEventListener(
          "selected-store:changed",
          this._onSelectedStoreChanged,
        );

        this._bindToggle();
        this._fetchStoreList();
      }

      disconnectedCallback() {
        window.removeEventListener(
          "selected-store:changed",
          this._onSelectedStoreChanged,
        );
      }

      _onSelectedStoreChanged(event) {
        const store = event?.detail?.store;

        if (!store || !store.name) {
          this._restoreSelectedStore();
          return;
        }

        let hasMatch = false;
        this.querySelectorAll(".pickup-availability-cart__item").forEach(
          (li) => {
            const isSelected = li.dataset.storeName === store.name;
            li.classList.toggle("is-selected", isSelected);
            if (isSelected) hasMatch = true;
          },
        );

        if (hasMatch) {
          this._updatePreview(store.name);
        }
      }

      _getStoreListItem(storeName) {
        return Array.from(
          this.querySelectorAll(".pickup-availability-cart__item"),
        ).find((li) => li.dataset.storeName === storeName);
      }

      /* ── toggle expand / collapse ──────────────────────────── */

      _bindToggle() {
        const toggleBtn = this.querySelector(
          ".pickup-availability-cart__toggle",
        );
        const list = this.querySelector(".pickup-availability-cart__list");
        if (!toggleBtn || !list) return;

        toggleBtn.addEventListener("click", () => {
          const expanded = toggleBtn.getAttribute("aria-expanded") === "true";
          toggleBtn.setAttribute("aria-expanded", String(!expanded));
          list.style.display = expanded ? "none" : "block";
        });
      }

      /* ── fetch store list via Shopify section render ────────── */

      async _fetchStoreList() {
        try {
          const cartRes = await fetch("/cart.js");
          const cartData = cartRes.ok ? await cartRes.json() : { items: [] };
          const cartItems = (cartData.items || []).filter((item) => item.sku);
          const variantIds = [
            this._variantId,
            ...cartItems.map((item) => item.variant_id),
          ].filter(Boolean);
          const uniqueVariantIds = [...new Set(variantIds)];

          if (uniqueVariantIds.length === 0) return;

          let sourceList = null;

          for (const variantId of uniqueVariantIds) {
            const storeUrl =
              this._rootUrl +
              "variants/" +
              variantId +
              "/?section_id=pickup-availability";

            const storeRes = await fetch(storeUrl);
            if (!storeRes.ok) continue;

            const text = await storeRes.text();
            const doc = new DOMParser().parseFromString(text, "text/html");
            const candidateList = doc.querySelector(
              ".pickup-availability-list",
            );

            if (!candidateList) continue;

            const candidateItems = candidateList.querySelectorAll(
              ".pickup-availability-list__item",
            );

            if (candidateItems.length === 0) continue;

            sourceList = candidateList;
            break;
          }

          if (!sourceList) return;

          const items = sourceList.querySelectorAll(
            ".pickup-availability-list__item",
          );
          this._renderStores(items);

          if (cartItems.length > 0) {
            this._loadAllStoreInventory(cartItems);
          }
        } catch (err) {
          console.error("[pickup-cart] Failed to load store list:", err);
        }
      }

      /* ── inject product-list styles once ───────────────────── */

      _injectStyles() {
        if (document.getElementById("pac-product-styles")) return;
        const s = document.createElement("style");
        s.id = "pac-product-styles";
        s.textContent = [
          ".pac-product-list{margin:.6rem 0 0;padding:0;list-style:none;display:flex;flex-direction:column;gap:.35rem;}",
          ".pac-product-item{display:flex;align-items:center;gap:.6rem;font-size:1.1rem;line-height:1.3;}",
          ".pac-product-item__img{width:28px;height:28px;object-fit:cover;border-radius:3px;flex-shrink:0;}",
          ".pac-product-item__name{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}",
          ".pac-product-item__status{flex-shrink:0;font-size:1.1rem;font-weight:700;}",
          ".pac-product-item__status--ok{color:#108043;}",
          ".pac-product-item__status--no{color:#c4111f;}",
          ".pac-product-checking{font-size:1.1rem;color:rgba(var(--color-foreground),.4);margin:.4rem 0 0;}",
        ].join("");
        document.head.appendChild(s);
      }

      /* ── fetch Shopify pickup availability for one variant ──── */
      // Returns Map<storeName, boolean> using the section render endpoint.
      // Zero external API calls – reads Shopify's own inventory data.

      async _fetchVariantAvailability(variantId) {
        const url =
          this._rootUrl +
          "variants/" +
          variantId +
          "/?section_id=pickup-availability";
        try {
          const res = await fetch(url);
          if (!res.ok) return new Map();
          const text = await res.text();
          const doc = new DOMParser().parseFromString(text, "text/html");
          const map = new Map();
          doc
            .querySelectorAll(".pickup-availability-list__item")
            .forEach((li) => {
              const name = li.dataset.storeName || li.dataset.name || "";
              if (!name) return;
              // Shopify only renders the radio input when pickup IS available
              const available = !!li.querySelector(".select-store-input");
              map.set(name, available);
            });
          return map;
        } catch {
          return new Map();
        }
      }

      /* ── check every product against every store ────────────── */
      // Cost: 1 Shopify section-render request per unique variant in the cart.
      // (e.g. 2 cart items = 2 requests, regardless of number of stores)

      async _loadAllStoreInventory(cartItems) {
        this._injectStyles();
        const listEl = this.querySelector(".pickup-availability-cart__list");
        if (!listEl) return;

        const storeLis = listEl.querySelectorAll(
          ".pickup-availability-cart__item",
        );

        // Show loading placeholder on every store row
        storeLis.forEach((li) => {
          const content = li.querySelector(".pac-store-content");
          if (!content) return;
          const p = document.createElement("p");
          p.className = "pac-product-checking";
          p.textContent = "Checking availability\u2026";
          content.appendChild(p);
        });

        // Deduplicate variants – one fetch per unique variant ID
        const uniqueVariants = [
          ...new Map(cartItems.map((i) => [i.variant_id, i])).values(),
        ];

        // Fetch all variant availability maps in parallel
        const variantMaps = await Promise.all(
          uniqueVariants.map(async (item) => ({
            item,
            storeMap: await this._fetchVariantAvailability(item.variant_id),
          })),
        );

        // Store for use when a store is later selected
        this._variantMaps = variantMaps;

        // Remove placeholders and render per-store product list
        storeLis.forEach((li) => {
          const content = li.querySelector(".pac-store-content");
          content?.querySelector(".pac-product-checking")?.remove();

          const storeName = li.dataset.storeName;
          if (!storeName || !content) return;

          const ul = document.createElement("ul");
          ul.className = "pac-product-list";

          variantMaps.forEach(({ item, storeMap }) => {
            const available = storeMap.has(storeName)
              ? storeMap.get(storeName)
              : null;

            const li2 = document.createElement("li");
            li2.className = "pac-product-item";

            const img = document.createElement("img");
            img.className = "pac-product-item__img";
            img.src = item.image || "";
            img.alt = "";
            img.loading = "lazy";

            const name = document.createElement("span");
            name.className = "pac-product-item__name";
            name.textContent = item.product_title || item.title || "";

            const status = document.createElement("span");
            if (available === null) {
              status.className = "pac-product-item__status";
              status.textContent = "\u2013";
            } else if (available) {
              status.className =
                "pac-product-item__status pac-product-item__status--ok";
              status.textContent = "\u2713";
            } else {
              status.className =
                "pac-product-item__status pac-product-item__status--no";
              status.textContent = "\u2717";
            }

            li2.appendChild(img);
            li2.appendChild(name);
            li2.appendChild(status);
            ul.appendChild(li2);
          });

          content.appendChild(ul);
        });
      }

      /* ── render store items (all clickable) ────────────────── */

      _renderStores(sourceItems) {
        const list = this.querySelector(".pickup-availability-cart__list");
        if (!list) return;

        list.innerHTML = "";

        sourceItems.forEach((source) => {
          const storeName =
            source.dataset.storeName || source.dataset.name || "";
          const radioInput = source.querySelector(".select-store-input");
          // Prefer siteup from the <li> itself (available for ALL stores including
          // unavailable ones). Fall back to the radio input for older renders.
          const siteCode =
            source.dataset.siteup ||
            (radioInput ? radioInput.dataset.siteup || "" : "");

          // Shopify marks a store as "pickup available" only when a radio input exists.
          // We render ALL stores as clickable regardless; the API call decides fate.
          const isShopifyAvailable = !!radioInput;

          const addressEl = source.querySelector(
            ".pickup-availability-address",
          );
          const storeHoursEl = source.querySelector(".store-hours");

          const addressHtml = addressEl ? addressEl.innerHTML : "";
          const storeHoursHtml = storeHoursEl ? storeHoursEl.innerHTML : "";

          const statusHtml = isShopifyAvailable
            ? '<span class="pac-status pac-status--available">Pickup available – usually ready in 3 hours</span>'
            : '<span class="pac-status pac-status--unavailable">Pickup currently unavailable</span>';

          const li = document.createElement("li");
          li.className = "pickup-availability-cart__item";
          li.dataset.storeName = storeName;
          li.dataset.siteCode = siteCode;
          li.dataset.shopifyAvailable = String(isShopifyAvailable);

          li.innerHTML =
            '<button class="pickup-availability-cart__store-btn" type="button"' +
            ' data-store-name="' +
            escHtml(storeName) +
            '"' +
            ' data-site-code="' +
            escHtml(siteCode) +
            '">' +
            '<span class="pac-fake-checkbox" aria-hidden="true"></span>' +
            '<div class="pac-store-content">' +
            '<span class="pac-store-name">' +
            escHtml(storeName) +
            "</span>" +
            statusHtml +
            '<address class="pac-store-address">' +
            addressHtml +
            "</address>" +
            (storeHoursHtml
              ? '<div class="pac-store-hours">' + storeHoursHtml + "</div>"
              : "") +
            "</div>" +
            "</button>";

          list.appendChild(li);
        });

        // After rendering: restore saved selection, then wire clicks.
        this._restoreSelectedStore();
        this._bindStoreClicks();
      }

      /* ── restore localStorage selection ────────────────────── */

      _restoreSelectedStore() {
        let store;
        try {
          const raw =
            localStorage.getItem("selectedStore") ||
            localStorage.getItem("selectedStoreCart");
          if (!raw) return;
          store = JSON.parse(raw);

          // Backfill legacy cart-only key into the unified key.
          if (!localStorage.getItem("selectedStore") && store?.name) {
            localStorage.setItem("selectedStore", JSON.stringify(store));
          }
        } catch (e) {
          return;
        }

        if (!store || !store.name) return;

        this.querySelectorAll(".pickup-availability-cart__item").forEach(
          (li) => {
            if (li.dataset.storeName === store.name) {
              li.classList.add("is-selected");
              this._updatePreview(store.name);
            }
          },
        );
      }

      /* ── bind click events on the store list ───────────────── */

      _bindStoreClicks() {
        const list = this.querySelector(".pickup-availability-cart__list");
        if (!list || list.dataset.pacBound) return;
        list.dataset.pacBound = "true";

        list.addEventListener("click", (e) => {
          const btn = e.target.closest(".pickup-availability-cart__store-btn");
          if (!btn) return;
          this._onStoreClick(btn.dataset.storeName, btn.dataset.siteCode);
        });
      }

      /* ── handle store click ─────────────────────────────────── */

      _onStoreClick(storeName, siteCode) {
        this._saveStore(storeName, siteCode);
      }

      /* ── persist selection & update UI ─────────────────────── */

      _saveStore(storeName, siteCode) {
        const storeData = { name: storeName, siteup: siteCode };

        try {
          localStorage.setItem("selectedStore", JSON.stringify(storeData));

          // Remove legacy key to prevent divergence between cart and PDP.
          localStorage.removeItem("selectedStoreCart");
        } catch (e) {
          /* storage quota exceeded – continue silently */
        }

        window.dispatchEvent(
          new CustomEvent("selected-store:changed", {
            detail: { store: storeData, source: "cart" },
          }),
        );

        // Mark selected item.
        this.querySelectorAll(".pickup-availability-cart__item").forEach(
          (li) => {
            li.classList.toggle(
              "is-selected",
              li.dataset.storeName === storeName,
            );
          },
        );

        this._updatePreview(storeName);

        // Collapse the store list after selection.
        const toggleBtn = this.querySelector(
          ".pickup-availability-cart__toggle",
        );
        const list = this.querySelector(".pickup-availability-cart__list");
        if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "false");
        if (list) list.style.display = "none";
      }

      /* ── update preview strip ───────────────────────────────── */

      _updatePreview(storeName) {
        const preview = this.querySelector(
          ".pickup-availability-preview-drawer",
        );
        if (!preview) return;
        const storeItem = this._getStoreListItem(storeName);
        const isShopifyAvailable =
          storeItem?.dataset.shopifyAvailable !== "false";

        // Build DOM nodes to avoid innerHTML injection risk.
        const wrap = document.createElement("div");
        wrap.className = "pac-selected-store";

        const dot = document.createElement("span");
        dot.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">' +
          '<circle cx="10" cy="10" r="9" fill="#108043"/>' +
          "</svg>";

        const textWrap = document.createElement("div");

        const line1 = document.createElement("p");
        line1.className = "caption-large";
        const strong = document.createElement("strong");
        strong.textContent = "Available at";
        line1.appendChild(strong);
        line1.appendChild(document.createTextNode(" – " + storeName));

        const line2 = document.createElement("p");
        line2.className = "caption";
        line2.textContent = isShopifyAvailable
          ? "Usually ready in 3 hours"
          : "Pickup currently unavailable";
        if (!isShopifyAvailable) {
          line2.style.color = "#c4111f";
        }

        textWrap.appendChild(line1);
        textWrap.appendChild(line2);
        wrap.appendChild(dot);
        wrap.appendChild(textWrap);

        preview.innerHTML = "";
        preview.appendChild(wrap);
      }
    },
  );
})();
